import React, { useState } from 'react';

import { faEnvelope, faPlugCircleXmark } from '@fortawesome/pro-regular-svg-icons';

import confirm from 'components/confirm';
import { Button } from 'components/ui/button';
import { EmailEnrollmentDialog } from './dialog';
import { Field } from '@buildoutinc/blueprint-react/ui/Field';
import { deleteRequest } from 'helpers/api';
import { showSnackbar } from 'components/ui/snackbar/helper';

import type { EmailEnrollment, UserProfilePaths } from 'components/users/types';

interface Props {
  email: string | null;
  enrollment: EmailEnrollment | null;
  openSetup: boolean;
  paths: UserProfilePaths;
  onChange: (enrollment: EmailEnrollment | null) => void;
}

export function EmailEnrollmentSection({ email, enrollment, openSetup, paths, onChange }: Props) {
  const verified = enrollment?.verificationStatus === 'verified';
  const pending = enrollment?.verificationStatus === 'pending';

  const [dialogOpen, setDialogOpen] = useState(openSetup && !verified);
  const [disconnecting, setDisconnecting] = useState(false);

  async function disconnect() {
    try {
      await confirm('Are you sure you want to stop emailing the assistant?', {
        buttonLabel: 'Disconnect',
        buttonStyle: 'danger',
        title: 'Disconnect email',
      });
    } catch {
      return;
    }

    try {
      setDisconnecting(true);
      await deleteRequest(paths.emailEnrollment);
      onChange(null);
    } catch {
      showSnackbar({ text: 'Something went wrong.', variant: 'danger' });
    } finally {
      setDisconnecting(false);
    }
  }

  function handleEnrolled(next: EmailEnrollment) {
    setDialogOpen(false);
    onChange(next);
  }

  let description: string;
  if (verified) {
    description = `Email ${enrollment.assistantAddress} from ${enrollment.address} to reach the assistant.`;
  } else if (pending) {
    description = `We emailed a link to ${enrollment.address}.`;
  } else {
    description = 'Reach the assistant by email from your own address.';
  }

  return (
    <Field className="form-group">
      <Field.Label>Email the assistant</Field.Label>

      {verified ? (
        <div className="d-flex align-items-center gap-2">
          <span>{enrollment.assistantAddress}</span>
          <Button
            disabled={disconnecting}
            icon={faPlugCircleXmark}
            label="Disconnect"
            variant="ghost"
            onClick={disconnect}
          />
        </div>
      ) : (
        <div className="d-flex align-items-center gap-2">
          <Button
            icon={faEnvelope}
            label={pending ? 'Send again' : 'Set up email'}
            variant="outline"
            onClick={() => setDialogOpen(true)}
          />
          {pending && <span className="text-muted">Check your email for a link</span>}
        </div>
      )}

      <Field.Description>{description}</Field.Description>

      {dialogOpen && (
        <EmailEnrollmentDialog
          address={enrollment?.address || email || ''}
          path={paths.emailEnrollment}
          onClose={() => setDialogOpen(false)}
          onEnrolled={handleEnrolled}
        />
      )}
    </Field>
  );
}
